import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { FUENTE, useTema } from './tema';
import { tareasDeZona, diaDeISO, isoDeDia, claveDeTarea } from './tareasDeLaMinuta';
import SelectorDePersona from './SelectorDePersona';
import {
  planesDisponibles, bucketsDePlan, miembrosDelGrupo, personasDelDirectorio,
  emparejarResponsables, crearTarea
} from '../../services/planner';

/**
 * EnviarAPlanner — llevar a Planner los compromisos de la tabla del acta.
 *
 * Lo que gobierna esta pantalla es que **crear una tarea no se deshace**: queda
 * en el plan de un equipo, notifica a quien se le asigne y no hay un botón para
 * volver atrás. Así que antes de crear nada se enseña qué se va a crear, a
 * nombre de quién y para cuándo, y se deja cambiarlo.
 *
 * De ahí las tres reglas:
 *
 *   · **El responsable se confirma, no se adivina.** En el acta pone "Iván R." y
 *     en el directorio hay tres Iván. Cuando hay más de un candidato se pregunta
 *     y la tarea queda sin asignar hasta que alguien elija. Asignar por orden
 *     alfabético es peor que no asignar: nadie revisa una tarea que cree que no
 *     es suya.
 *   · **Solo se busca dentro del equipo dueño del plan**, que es a quien Planner
 *     deja asignar. Buscar en todo el directorio además exigiría un permiso que
 *     esta aplicación no pide.
 *   · **Las filas vacías no se crean.** Las plantillas traen filas de relleno, y
 *     una tarea sin texto en un plan compartido es basura que alguien tendrá que
 *     borrar.
 */

const estilos = (C) => ({
  velo: {
    position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(2,6,23,.62)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18
  },
  caja: {
    width: 860, maxWidth: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
    background: C.fondo, border: `1px solid ${C.borde}`, borderRadius: 13,
    boxShadow: '0 22px 60px rgba(0,0,0,.42)'
  },
  cabecera: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '15px 20px',
    borderBottom: `1px solid ${C.borde}`
  },
  titulo: { margin: 0, fontSize: 15.5, fontWeight: 600, color: C.texto },
  cerrar: {
    marginLeft: 'auto', background: 'transparent', border: 'none', color: C.suave,
    fontSize: 19, cursor: 'pointer', lineHeight: 1, padding: '2px 6px'
  },
  cuerpo: { padding: '15px 20px', overflowY: 'auto' },
  fila: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 13 },
  etiqueta: { fontSize: 12.5, color: C.suave, minWidth: 74 },
  select: {
    padding: '6px 10px', borderRadius: 7, fontSize: 13, fontFamily: FUENTE, maxWidth: 420,
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`
  },
  tablaCaja: { border: `1px solid ${C.borde}`, borderRadius: 9, overflow: 'hidden' },
  tabla: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '8px 10px', fontSize: 11.5, fontWeight: 600, color: C.tenue,
    background: C.panelAlto, borderBottom: `1px solid ${C.borde}`
  },
  td: { padding: '8px 10px', fontSize: 13, color: C.texto, borderBottom: `1px solid ${C.borde}`, verticalAlign: 'top' },
  tarea: { lineHeight: 1.45 },
  // De qué tabla del acta sale lo que viene debajo.
  grupo: {
    padding: '7px 10px', fontSize: 11.5, fontWeight: 600, letterSpacing: .3,
    color: C.tenue, background: C.panelAlto, borderBottom: `1px solid ${C.borde}`,
    textTransform: 'uppercase'
  },
  chico: { display: 'block', fontSize: 11.5, color: C.tenue, marginTop: 3, lineHeight: 1.45 },
  ojo: { fontSize: 11.5, color: C.atencion, marginTop: 3, display: 'block', lineHeight: 1.45 },

  // Quien ya esta asignado, como fichas. Caben varios: un acuerdo se reparte.
  fichas: { display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 5 },
  ficha: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '2px 4px 2px 8px', borderRadius: 20, fontSize: 12,
    background: C.panelAlto, border: `1px solid ${C.borde}`, color: C.texto,
    maxWidth: 190
  },
  quitarFicha: {
    background: 'transparent', border: 'none', cursor: 'pointer', color: C.tenue,
    fontSize: 14, lineHeight: 1, padding: '0 2px', fontFamily: FUENTE
  },
  selectFila: {
    padding: '4px 7px', borderRadius: 6, fontSize: 12.5, fontFamily: FUENTE, maxWidth: 220,
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`
  },
  fechaFila: {
    padding: '4px 7px', borderRadius: 6, fontSize: 12.5, fontFamily: FUENTE, width: '100%',
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`,
    // El icono del calendario es negro sobre fondo oscuro en Chrome si no se
    // le dice lo contrario.
    colorScheme: 'dark'
  },
  pie: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    padding: '13px 20px', borderTop: `1px solid ${C.borde}`
  },
  boton: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 15px',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 8,
    color: C.texto, fontSize: 13.5, fontFamily: FUENTE, cursor: 'pointer'
  },
  botonAcento: { background: C.acento, borderColor: C.acento, color: C.acentoTexto },
  estado: (malo) => ({ fontSize: 12.5, lineHeight: 1.5, color: malo ? C.error : C.suave }),
  hecho: { fontSize: 12.5, color: C.exito },
  enlace: { color: C.acentoClaro }
});

const SIN_ASIGNAR = '';

const EnviarAPlanner = ({
  // Las tablas de tareas del acta: [{ id, titulo, filas, columnas }]. En plural
  // porque un acta trae normalmente dos —los acuerdos y el parking lot de temas
  // pendientes— y las dos son compromisos que alguien puede querer en Planner.
  zonas = [], fechaReunion = null, tokenGraph, titulo = '', onCerrar,
  // Las que esta acta ya mandó a Planner, y cómo apuntar las nuevas.
  yaCreadas = [], onRegistrar = null
}) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const [planes, setPlanes] = useState(null);
  const [planId, setPlanId] = useState('');
  const [buckets, setBuckets] = useState([]);
  const [bucketId, setBucketId] = useState('');
  const [miembros, setMiembros] = useState([]);
  // Todo el directorio. Va aparte de los miembros porque no significan lo
  // mismo: a los del equipo se les puede asignar, al resto no.
  const [directorio, setDirectorio] = useState([]);
  // Todo esto se indexa por `clave` («zona:fila») y no por número de fila: con
  // dos tablas, la fila 0 de una y la fila 0 de la otra serían la misma casilla.
  //
  // En `asignado`, «no está la clave» y «está con cadena vacía» NO son lo mismo:
  // lo primero es «aún no se ha decidido» y lo segundo es «se decidió que nadie».
  // De esa diferencia depende que la propuesta automática llegue a aplicarse.
  // clave → lista de ids. Lista y no un id suelto porque un acuerdo se reparte:
  // «lo ven Ana y Luis» es una frase normal en una reunion, y con un solo hueco
  // habia que elegir a uno —dejando al otro sin tarea y sin aviso— o crear dos
  // tareas iguales que despues nadie sabe si son la misma.
  const [asignado, setAsignado] = useState({});

  // ── Responsables de fuera de la organizacion ────────────────────────────
  //
  // A una reunion viene gente que no esta en el directorio: un cliente, un
  // proveedor, el despacho que lleva el contrato. Se les acuerdan compromisos
  // igual que a los de dentro, y el acta los nombra igual.
  //
  // Lo que NO se puede hacer es crearles una tarea. Planner solo asigna a
  // miembros del grupo dueño del plan; a un externo no hay a quien asignarsela,
  // y crearla sin responsable seria peor que no crearla: una tarea huerfana en
  // un tablero de la empresa, que nadie reclama y que al revisarla parece un
  // olvido en vez de un acuerdo con alguien de fuera.
  //
  // Asi que su compromiso vive en el acta —que es donde esta firmado— y la fila
  // se queda sin enlace a Planner, dicho en la pantalla para que se vea que fue
  // una decision y no un fallo.
  const [externo, setExterno] = useState({});    // clave → nombre escrito a mano
  const [vence, setVence] = useState({});         // clave → «AAAA-MM-DD», editable
  const [marcada, setMarcada] = useState({});     // clave → si se crea
  const [cargando, setCargando] = useState(true);
  const [creando, setCreando] = useState(false);
  const [fallo, setFallo] = useState(null);
  const [resultado, setResultado] = useState(null);

  // La fecha de la reunión completa los años que el acta no escribe ("7
  // septiembre"). Puede llegar como Date o como texto ISO según de dónde venga
  // la reunión, y colarla sin convertir dejaría el cálculo contra la fecha de
  // hoy: en enero, todos los vencimientos del acta de diciembre saltarían de año.
  const referencia = useMemo(() => {
    const d = fechaReunion ? new Date(fechaReunion) : null;
    return d && !isNaN(d.getTime()) ? d : new Date();
  }, [fechaReunion]);

  // Las tareas que salen de las tablas del acta, ya interpretadas y en el orden
  // en que están en el documento.
  const tareas = useMemo(
    () => zonas.flatMap(z => tareasDeZona(z.filas, z.columnas, referencia, z.id))
      .filter(t => !t.vacia),
    [zonas, referencia]
  );

  // Solo se ponen separadores cuando de verdad hay más de una tabla con algo
  // dentro. Con una sola, un encabezado repitiendo el nombre de la tabla que se
  // acaba de pulsar es ruido.
  const zonasConTareas = useMemo(() => {
    const vistas = [];
    for (const z of zonas) {
      if (tareas.some(t => t.zona === z.id)) vistas.push(z);
    }
    return vistas;
  }, [zonas, tareas]);

  // ── Lo que ya tiene tarea ─────────────────────────────────────────────
  //
  // Se busca por el texto del compromiso y no por la fila: las filas se mueven
  // —se inserta un acuerdo en medio, se borra otro— y vincular por posición
  // acabaría enseñando el enlace de un compromiso sobre otro distinto, que es
  // peor que no enseñar ninguno.
  const creadaDe = useMemo(() => {
    const porClave = new Map();
    for (const t of yaCreadas) if (t && t.clave) porClave.set(t.clave, t);
    return (tarea) => porClave.get(claveDeTarea(tarea.titulo)) || null;
  }, [yaCreadas]);

  // Todas marcadas de entrada menos las que ya existen: quien abre esto viene a
  // crearlas, y desmarcar dos es menos trabajo que marcar ocho. Las que ya
  // están, desmarcadas y sin poder marcarse: crear una tarea no se deshace, y
  // la que hay ya está en el plan de alguien.
  useEffect(() => {
    setMarcada(Object.fromEntries(tareas.map(t => [t.clave, !creadaDe(t)])));
  }, [tareas, creadaDe]);

  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const lista = await planesDisponibles(await tokenGraph());
        if (!vigente) return;
        setPlanes(lista);
        if (lista.length > 0) setPlanId(lista[0].id);
      } catch (e) {
        if (vigente) setFallo(`No se pudieron leer tus planes de Planner: ${e.message}`);
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => { vigente = false; };
  }, [tokenGraph]);

  // El directorio se pide una vez y no por plan: es el mismo para todos, y
  // volver a bajarlo al cambiar de equipo serían segundos de espera para
  // obtener exactamente la misma lista.
  //
  // Si falla no se dice nada: significa que el permiso de leer el directorio no
  // está concedido, y entonces quedan los del equipo — que son los únicos a
  // quienes Planner deja asignar de todas formas. Un error rojo por no poder
  // ofrecer gente a la que la asignación iba a fallar estorbaría más que ayudar.
  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const gente = await personasDelDirectorio(await tokenGraph());
        if (vigente) setDirectorio(gente);
      } catch {
        if (vigente) setDirectorio([]);
      }
    })();
    return () => { vigente = false; };
  }, [tokenGraph]);

  const plan = useMemo(() => (planes || []).find(p => p.id === planId) || null, [planes, planId]);

  /**
   * Quién se puede elegir: el equipo primero, y detrás el resto del directorio.
   *
   * En ese orden a propósito. Los del equipo son a quienes Planner acepta; los
   * demás se ofrecen porque se pidió verlos, pero salen marcados y después,
   * para que elegir a uno sea una decisión y no un descuido de ordenación
   * alfabética.
   */
  // Hay planes que no cuelgan de ningún equipo —los que crea la aplicación
  // nueva de Planner y los compartidos de Teams—. Ahí no es que el equipo esté
  // vacío: es que no hay equipo, y la diferencia importa. Sin esto, todo el
  // directorio saldría marcado como «de fuera» y cada responsable del acta
  // llevaría un aviso de que no está en el equipo. Avisos inventados.
  const sabemosElEquipo = Boolean(plan && plan.grupoId);

  const elegibles = useMemo(() => {
    const enEquipo = new Set(miembros.map(m => m.id));
    return [
      ...miembros.map(m => ({ ...m, enEquipo: true })),
      ...directorio.filter(p => !enEquipo.has(p.id))
        .map(p => ({ ...p, enEquipo: sabemosElEquipo ? false : undefined }))
    ];
  }, [miembros, directorio, sabemosElEquipo]);


  // Al cambiar de plan cambian sus columnas y, sobre todo, su gente: el mismo
  // "Reyes" puede estar en un equipo y no en otro.
  useEffect(() => {
    if (!plan) return undefined;
    let vigente = true;
    (async () => {
      try {
        const token = await tokenGraph();
        const [cubos, gente] = await Promise.all([
          bucketsDePlan(token, plan.id),
          miembrosDelGrupo(token, plan.grupoId)
        ]);
        if (!vigente) return;
        setBuckets(cubos);
        setBucketId(cubos.length > 0 ? cubos[0].id : '');
        setMiembros(gente);
      } catch (e) {
        if (vigente) setFallo(`No se pudo leer el plan: ${e.message}`);
      }
    })();
    return () => { vigente = false; };
  }, [plan, tokenGraph]);

  // Quién es cada responsable del acta dentro de ESE equipo. Cuando el plan no
  // tiene equipo se busca en el directorio, que es de donde salen los
  // elegibles: emparejar contra una lista vacía no daría «no se sabe», daría
  // «no está», que es una respuesta distinta y falsa.
  const dondeBuscar = sabemosElEquipo ? miembros : directorio;
  const emparejados = useMemo(() => {
    const salida = {};
    // `emparejarResponsables` y no `emparejarPersona`: una casilla puede decir
    // «Ana y Luis», y eso son dos personas, no un nombre que no encaja.
    for (const t of tareas) salida[t.clave] = emparejarResponsables(t.responsable, dondeBuscar);
    return salida;
  }, [tareas, dondeBuscar]);

  /**
   * Lo inequívoco se propone ya elegido; lo dudoso se queda sin asignar.
   *
   * ── Por qué esto no llegaba a pasar nunca ────────────────────────────
   *
   * Este efecto solo tocaba las casillas sin decidir, que es lo correcto: una
   * vez que alguien elige a mano, la propuesta no debe volver a pisarla. Pero
   * cuando no encontraba a nadie escribía «sin asignar» en la casilla, y eso la
   * dejaba decidida.
   *
   * Al abrir la ventana, los miembros del plan todavía no han llegado —se piden
   * por red—, así que el emparejador no encuentra a nadie y el efecto marcaba
   * TODAS las filas como «sin asignar». Cuando las personas por fin llegaban y
   * el emparejador acertaba, ya no quedaba ninguna casilla por decidir y no se
   * proponía nada. La tarea se creaba sin responsable aunque el acta lo dijera
   * con todas sus letras.
   *
   * Ahora solo se escribe cuando hay a quién: no encontrar a nadie deja la
   * casilla como estaba, sin decidir, y la propuesta se aplica en cuanto llega
   * la gente. Quien elija «sin asignar» a mano sí la decide, y eso se respeta.
   */
  useEffect(() => {
    setAsignado(previo => {
      let cambio = false;
      const siguiente = { ...previo };
      for (const t of tareas) {
        if (siguiente[t.clave] !== undefined) continue;
        const e = emparejados[t.clave];
        if (e && e.estado === 'unico') {
          // Lista de uno: la propuesta es una persona, pero el hueco admite
          // varias y quien revisa puede añadir a la segunda sin quitar a esta.
          siguiente[t.clave] = [e.candidatos[0].id];
          cambio = true;
        } else if (e && e.estado === 'reparto') {
          // La casilla nombraba a varias y todas se reconocieron: se proponen
          // TODAS. Dejar solo a una repartiria mal un acuerdo que el acta ya
          // decia repartido, y nadie lo notaria.
          siguiente[t.clave] = e.candidatos.map(c => c.id);
          cambio = true;
        }
      }
      // Sin esto se devolvería un objeto nuevo en cada pasada y el efecto se
      // volvería a disparar solo, indefinidamente.
      return cambio ? siguiente : previo;
    });
  }, [emparejados, tareas]);

  // Al cambiar de plan, lo elegido antes ya no vale: son otras personas. No se
  // limpia al arrancar, cuando `planId` pasa de vacío al primer plan, porque
  // entonces no hay nada elegido que perder.
  const planAnterior = useRef('');
  useEffect(() => {
    if (planAnterior.current && planAnterior.current !== planId) setAsignado({});
    planAnterior.current = planId;
  }, [planId]);

  // ── La fecha se puede poner aquí ──────────────────────────────────────
  //
  // Se leía del acta y, si no se entendía —«Por definir», «Al cierre del mes»,
  // la celda en blanco—, la tarea se creaba sin plazo y no había forma de
  // arreglarlo sin salir a Planner a editarla una por una. Y una tarea sin
  // fecha en un plan compartido es una tarea que nadie mira.
  //
  // Lo que el acta dice sigue mandando como propuesta: casi siempre acierta, y
  // esto es para el resto.
  useEffect(() => {
    setVence(previo => {
      const siguiente = { ...previo };
      for (const t of tareas) {
        if (siguiente[t.clave] === undefined) siguiente[t.clave] = diaDeISO(t.vence);
      }
      return siguiente;
    });
  }, [tareas]);

  const elegidas = tareas.filter(t => marcada[t.clave]);

  // Lo elegido que Planner no va a aceptar, contado ANTES de crear nada. Va
  // aqui y no arriba porque depende de `elegidas`, que se declara justo encima:
  // adelantarlo seria leer una constante antes de existir.
  // Solo se puede decir que alguien es de fuera si se sabe quién es de dentro.
  // En un plan sin equipo no se sabe, así que no se avisa: si Planner rechaza
  // al destinatario, `crearTarea` reintenta sin asignar y lo cuenta después.
  // Marcadas que SI van a Planner. Las de un externo se quedan fuera de todas
  // las cuentas de abajo: contarlas como «sin asignar» seria senalar como
  // descuido lo que se acaba de decidir, y el boton prometeria mas tareas de
  // las que va a crear.
  const aCrear = elegidas.filter(t => !externo[t.clave]);
  const externas = elegidas.filter(t => externo[t.clave]);

  const deFuera = !sabemosElEquipo ? [] : aCrear.filter(t => {
    const ids = asignado[t.clave] || [];
    return ids.length > 0 && ids.some(id => !miembros.some(m => m.id === id));
  });

  const crear = useCallback(async () => {
    setCreando(true);
    setFallo(null);
    const hechas = [];
    const fallidas = [];
    const omitidas = [];

    try {
      const token = await tokenGraph();
      // Una a una y en serie: Planner limita el ritmo, y en paralelo devuelve
      // 429 a la mitad. Además así lo que falle se puede nombrar.
      for (const t of elegidas) {
        // De fuera de la organizacion: su compromiso se queda en el acta. No se
        // llama a Planner siquiera —no hay a quien asignarsela— y se cuenta
        // aparte, para que el resumen final no diga «3 creadas» cuando fueron
        // dos y una se dejo a proposito.
        if (externo[t.clave]) {
          omitidas.push({ ...t, quien: externo[t.clave] });
          continue;
        }

        try {
          const creada = await crearTarea(token, {
            planId,
            bucketId: bucketId || null,
            titulo: t.titulo,
            asignadoA: asignado[t.clave] || [],
            // Lo que hay en la fila, que empieza siendo lo del acta y puede
            // haberse corregido a mano.
            vence: isoDeDia(vence[t.clave]),
            nota: titulo ? `Acordado en: ${titulo}` : null
          });
          hechas.push({ ...creada, fila: t.fila, clave: t.clave });

          // Se apunta ANTES de seguir con la siguiente. Si el navegador se
          // cierra a mitad, lo creado hasta aquí queda vinculado y no se
          // volverá a crear; apuntarlas todas al final dejaría tareas
          // existentes sin rastro, que es el duplicado de mañana.
          if (onRegistrar) {
            try {
              await onRegistrar({
                title: t.titulo,
                rowIndex: t.fila,
                taskId: creada.id,
                url: creada.url,
                planId,
                planName: plan ? plan.titulo : null,
                // El primero: la columna guarda un correo, no una lista. Lo
                // que vale es lo que quedo en Planner, que si lleva a los dos.
                assignedTo: (asignado[t.clave] || [])[0] || null
              });
            } catch {
              // La tarea existe en Planner: eso ya no se deshace. Que no se
              // haya podido apuntar el vínculo es molesto —se podrá volver a
              // crear— pero no es motivo para dar la tarea por fallida.
            }
          }
        } catch (e) {
          fallidas.push({ titulo: t.titulo, motivo: e.message });
        }
      }
      setResultado({ hechas, fallidas, omitidas });
    } catch (e) {
      setFallo(`No se pudo crear en Planner: ${e.message}`);
    } finally {
      setCreando(false);
    }
  }, [elegidas, planId, bucketId, asignado, externo, vence, tokenGraph, titulo, plan, onRegistrar]);

  const cuerpo = () => {
    if (cargando) return <p style={S.estado()}>Buscando tus planes de Planner…</p>;

    if (resultado) {
      return (
        <>
          <p style={S.hecho}>
            {resultado.hechas.length === 0
              ? 'No se creó ninguna tarea.'
              : `Creadas ${resultado.hechas.length} ${resultado.hechas.length === 1 ? 'tarea' : 'tareas'} en «${plan ? plan.titulo : ''}».`}
          </p>
          <ul style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: C.texto }}>
            {resultado.hechas.map(h => (
              <li key={h.id}>
                <a href={h.url} target="_blank" rel="noreferrer" style={S.enlace}>{h.titulo}</a>
                {/* Que una tarea quedara sin dueno no basta con decirlo: si
                    se pidio uno y Planner lo rechazo, hay que contar cual fue
                    el motivo. Una tarea sin asignar que se creyo asignada es de
                    las cosas que no se descubren hasta que vence. */}
                {!h.asignada && (
                  <span style={S.chico}>
                    {h.rechazoDelDestinatario
                      ? `sin asignar — esa persona no está en el equipo del plan`
                      : 'sin asignar'}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {resultado.fallidas.length > 0 && (
            <>
              <p style={{ ...S.estado(true), marginTop: 14 }}>
                No se pudieron crear {resultado.fallidas.length}:
              </p>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12.5, lineHeight: 1.6, color: C.suave }}>
                {resultado.fallidas.map((f, i) => <li key={i}>{f.titulo} — {f.motivo}</li>)}
              </ul>
            </>
          )}

          {/* Las que no se crearon a proposito. Van separadas de las que
              fallaron —y despues, para no alarmar— porque no son lo mismo: una
              fallida hay que reintentarla y esta no. Pero se nombran igual: un
              compromiso que no esta en ningun tablero conviene saber cual es. */}
          {resultado.omitidas.length > 0 && (
            <>
              <p style={{ ...S.estado(), marginTop: 14 }}>
                {resultado.omitidas.length === 1 ? 'Una queda' : `Quedan ${resultado.omitidas.length}`} solo
                en el acta, por ser de alguien de fuera de la organización:
              </p>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12.5, lineHeight: 1.6, color: C.suave }}>
                {resultado.omitidas.map((o, i) => <li key={i}>{o.titulo} — {o.quien}</li>)}
              </ul>
            </>
          )}
        </>
      );
    }

    if (planes && planes.length === 0) {
      return (
        <p style={S.estado()}>
          No apareció ningún plan de Planner en tus equipos. Los planes cuelgan de un grupo de
          Microsoft 365: hace falta pertenecer a uno que tenga Planner.
        </p>
      );
    }

    if (tareas.length === 0) {
      return (
        <p style={S.estado()}>
          La tabla de tareas del acta está vacía. Escribe los compromisos en ella y vuelve aquí.
        </p>
      );
    }

    return (
      <>
        <div style={S.fila}>
          <span style={S.etiqueta}>Plan</span>
          <select style={S.select} value={planId} onChange={(e) => setPlanId(e.target.value)}>
            {(planes || []).map(p => (
              <option key={p.id} value={p.id}>{p.grupo ? `${p.grupo} · ${p.titulo}` : p.titulo}</option>
            ))}
          </select>

          {buckets.length > 0 && (
            <>
              <span style={S.etiqueta}>Columna</span>
              <select style={S.select} value={bucketId} onChange={(e) => setBucketId(e.target.value)}>
                {buckets.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </>
          )}
        </div>

        <div style={S.tablaCaja}>
          <table style={S.tabla}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: 34 }} aria-label="Crear" />
                <th style={S.th}>Tarea</th>
                <th style={{ ...S.th, width: 230 }}>Se le asigna a</th>
                <th style={{ ...S.th, width: 168 }}>Vence</th>
              </tr>
            </thead>
            <tbody>
              {tareas.map((t, i) => {
                const e = emparejados[t.clave] || { estado: 'ninguno', candidatos: [] };
                // Un encabezado cada vez que cambia la tabla de la que salen,
                // y solo si hay más de una: sin él, los acuerdos y los temas
                // pendientes se leen como una sola lista y no hay forma de
                // saber de dónde viene cada línea.
                const abre = zonasConTareas.length > 1
                  && (i === 0 || tareas[i - 1].zona !== t.zona);
                const zona = abre ? zonasConTareas.find(z => z.id === t.zona) : null;
                return (
                  <React.Fragment key={t.clave}>
                  {abre && (
                    <tr>
                      <td style={S.grupo} colSpan={4}>
                        {(zona && zona.titulo) || 'Otra tabla'}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td style={S.td}>
                      <input
                        type="checkbox"
                        checked={Boolean(marcada[t.clave])}
                        disabled={Boolean(creadaDe(t))}
                        title={creadaDe(t) ? 'Esta tarea ya existe en Planner' : ''}
                        onChange={(ev) => setMarcada(m => ({ ...m, [t.clave]: ev.target.checked }))}
                      />
                    </td>
                    <td style={{ ...S.td, ...S.tarea }}>
                      {t.titulo}
                      {/* La que ya existe se enseña con su enlace en lugar de
                          ofrecerse otra vez. Ocultarla seria peor: quien busca
                          «¿esto se mando?» necesita verlo, y lo que necesita es
                          ir a ella, no crearla. */}
                      {creadaDe(t) && (
                        <span style={S.chico}>
                          ya está en Planner ·{' '}
                          <a
                            href={creadaDe(t).url}
                            target="_blank"
                            rel="noreferrer"
                            style={S.enlace}
                          >
                            abrir la tarea
                          </a>
                          {creadaDe(t).plan ? ` · ${creadaDe(t).plan}` : ''}
                        </span>
                      )}
                    </td>
                    <td style={S.td}>
                      {/* Un desplegable a secas obligaba a recorrer con la
                          vista todo el grupo de Microsoft 365 dueño del plan
                          —que es mucha mas gente que la que fue a la reunion—
                          para encontrar a una persona. Con buscador se escribe
                          un trozo del nombre y ya esta. */}
                      {/* Fichas de quien ya esta, y debajo un selector para
                          añadir. Es la misma forma que tiene elegir firmantes,
                          y por el mismo motivo: con un solo hueco, poner a la
                          segunda persona obligaba a quitar a la primera. */}
                      {(asignado[t.clave] || []).length > 0 && (
                        <div style={S.fichas}>
                          {(asignado[t.clave] || []).map(id => {
                            const quien = elegibles.find(p => p.id === id);
                            return (
                              <span key={id} style={S.ficha}>
                                {quien ? quien.nombre : 'Alguien del equipo'}
                                <button
                                  style={S.quitarFicha}
                                  onClick={() => setAsignado(a => ({
                                    ...a, [t.clave]: (a[t.clave] || []).filter(x => x !== id)
                                  }))}
                                  aria-label={`Quitar a ${quien ? quien.nombre : 'esta persona'}`}
                                  title="Quitar"
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      <SelectorDePersona
                        personas={elegibles.filter(p => !(asignado[t.clave] || []).includes(p.id))}
                        valor={SIN_ASIGNAR}
                        textoMostrado={externo[t.clave] || ''}
                        textoLibre
                        vacio={(asignado[t.clave] || []).length > 0 ? '+ Añadir a otra persona' : 'Sin asignar'}
                        onCambio={(id, persona) => {
                          if (id) {
                            // Se añade a los que ya hay; no los sustituye.
                            setAsignado(a => ({
                              ...a,
                              [t.clave]: [...new Set([...(a[t.clave] || []), id])]
                            }));
                            setExterno(x => ({ ...x, [t.clave]: null }));
                            return;
                          }
                          // Sin id es alguien escrito a mano: no esta en el
                          // directorio, luego es de fuera. Se guarda su nombre
                          // aparte porque `asignado` solo entiende ids, y
                          // perderlo dejaria la fila como «sin asignar», que es
                          // lo contrario de lo que se acaba de decir.
                          if (persona && persona.nombre) {
                            setAsignado(a => ({ ...a, [t.clave]: [] }));
                            setExterno(x => ({ ...x, [t.clave]: persona.nombre }));
                          }
                        }}
                        etiqueta={`Se le asigna a, para: ${t.titulo}`}
                      />

                      {externo[t.clave] && (
                        <span style={S.ojo}>
                          «{externo[t.clave]}» es de fuera de la organización: queda
                          en el acta, sin tarea en Planner.
                        </span>
                      )}

                      {/* Se dice de dónde sale la propuesta y por qué, sobre todo
                          cuando NO hay propuesta: ahí es donde la pantalla vieja
                          elegía sola. */}
                      {t.responsable && e.estado === 'varios' && (
                        <span style={S.ojo}>
                          «{t.responsable}» encaja con {e.candidatos.length} personas
                          {sabemosElEquipo ? ' del equipo' : ' del directorio'}. Elige tú.
                        </span>
                      )}
                      {t.responsable && e.estado === 'ninguno' && (
                        <span style={S.ojo}>
                          {sabemosElEquipo
                            ? `«${t.responsable}» no está en este equipo.`
                            : `«${t.responsable}» no se encontró en el directorio.`}
                        </span>
                      )}
                      {!t.responsable && <span style={S.chico}>el acta no dice quién.</span>}
                    </td>
                    <td style={S.td}>
                      <input
                        type="date"
                        style={S.fechaFila}
                        value={vence[t.clave] || ''}
                        onChange={(ev) => setVence(v => ({ ...v, [t.clave]: ev.target.value }))}
                        aria-label={`Fecha de vencimiento de: ${t.titulo}`}
                      />

                      {/* Lo que decía el acta, cuando no se pudo entender. Se
                          enseña el texto original —no un «sin fecha» a secas—
                          porque a veces la respuesta está ahí: «fin de mes» le
                          dice a quien estuvo en la reunión qué día poner. */}
                      {t.fechaIlegible && (
                        <span style={S.ojo}>
                          El acta dice «{t.fechaTexto}». Ponla aquí si la sabes.
                        </span>
                      )}
                      {!t.fechaIlegible && !vence[t.clave] && (
                        <span style={S.chico}>sin plazo.</span>
                      )}
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <div style={S.velo} onClick={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
      <div style={S.caja} role="dialog" aria-label="Crear tareas en Planner">
        <div style={S.cabecera}>
          <h3 style={S.titulo}>Crear tareas en Planner</h3>
          <button style={S.cerrar} onClick={onCerrar} aria-label="Cerrar">×</button>
        </div>

        <div style={S.cuerpo}>{cuerpo()}</div>

        <div style={S.pie}>
          {!resultado && tareas.length > 0 && planes && planes.length > 0 && (
            <button
              style={{ ...S.boton, ...S.botonAcento }}
              onClick={crear}
              disabled={creando || aCrear.length === 0 || !planId}
            >
              {creando
                ? 'Creando…'
                : aCrear.length === 0
                  ? 'Nada que crear en Planner'
                  : `Crear ${aCrear.length} ${aCrear.length === 1 ? 'tarea' : 'tareas'}`}
            </button>
          )}
          <button style={S.boton} onClick={onCerrar}>{resultado ? 'Cerrar' : 'Cancelar'}</button>
          {fallo && <span style={S.estado(true)}>{fallo}</span>}
          {!fallo && !resultado && elegidas.length > 0 && (
            <span style={S.estado()}>
              {/* Lo que falta, contado antes de crear nada. Las dos cosas por
                  separado: una tarea sin dueño y una tarea sin plazo se pierden
                  de maneras distintas, y arreglarlas después cuesta salir a
                  Planner y editarlas de una en una. */}
              {[
                aCrear.filter(t => (asignado[t.clave] || []).length === 0).length > 0
                  ? `${aCrear.filter(t => (asignado[t.clave] || []).length === 0).length} sin asignar`
                  : null,
                aCrear.filter(t => !vence[t.clave]).length > 0
                  ? `${aCrear.filter(t => !vence[t.clave]).length} sin plazo`
                  : null,
                // Lo que Planner va a rechazar, dicho ANTES de pulsar. Enterarse
                // despues, en la lista de resultados, es enterarse tarde.
                deFuera.length > 0
                  ? `${deFuera.length} a gente de fuera del equipo (se crearán sin responsable)`
                  : null,
                // Y lo que no va a ir, por la misma razon: que se sepa ahora.
                externas.length > 0
                  ? `${externas.length} de gente externa (se quedan solo en el acta)`
                  : null
              ].filter(Boolean).join(' · ') || 'Todas con responsable y plazo.'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnviarAPlanner;
